import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { LoadingSpinner } from "../../components/Spinner";
import { PageType } from "../../constants";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { ticketingActions } from "../../redux/actions/ticketingActions";
import {
  getIsTicketingAdmin,
  getIsTicketingManager,
  getTicketingIsNotificationEmailSaving,
  getTicketingLoginEmail,
  getTicketingNotificationEmail,
  getTicketingNotificationEmailError,
  getTicketingNotificationEmailOptOut,
  getTicketingNotificationEmailVerified,
  getTicketingSelectedAccessId,
} from "../../redux/selectors/ticketingSelectors";
import { AccessDeniedPage } from "../AccessDeniedPage/AccessDeniedPage";
import { useTicketingStrings } from "../TicketingPage/ticketingStrings";
import { useRequireTicketingAccess } from "../TicketingPage/useRequireTicketingAccess";
import { TeamsAdminSection } from "./TeamsAdminSection";
import * as styles from "./TicketingSettingsPage.module.css";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TicketingSettingsPage: React.FC = () => {
  const { isSignedIn, isVerifyChecked, isVerified } = useRequireTicketingAccess();
  const strings = useTicketingStrings();
  const dispatch = useDispatch();
  const history = useHistory();
  const notificationEmail = useSelector(getTicketingNotificationEmail);
  const loginEmail = useSelector(getTicketingLoginEmail);
  const isSaving = useSelector(getTicketingIsNotificationEmailSaving);
  const serverError = useSelector(getTicketingNotificationEmailError);
  const optOut = useSelector(getTicketingNotificationEmailOptOut);
  const verified = useSelector(getTicketingNotificationEmailVerified);
  const selectedAccessId = useSelector(getTicketingSelectedAccessId);
  const isAdmin = useSelector(getIsTicketingAdmin);
  const isManager = useSelector(getIsTicketingManager);

  // On mount: if no accessId in Redux, read from URL and trigger verify + set accessId
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!selectedAccessId && isVerifyChecked) {
      const urlAccessId = new URLSearchParams(window.location.search).get("accessid");
      if (urlAccessId) {
        dispatch(ticketingActions.setAccessId(urlAccessId));
      }
    }
  }, [dispatch, selectedAccessId, isVerifyChecked]);

  // Trigger admin check once accessId is available
  useEffect(() => {
    if (selectedAccessId) {
      dispatch(ticketingActions.fetchTeamsAdmin.start(selectedAccessId));
    }
  }, [dispatch, selectedAccessId]);

  // Email is considered verified if it matches login email or if explicitly verified
  const isEffectivelyVerified =
    verified ||
    (!!loginEmail &&
      !!notificationEmail &&
      loginEmail.toLowerCase() === notificationEmail.toLowerCase());

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [validationError, setValidationError] = useState<string | undefined>();

  const handleBack = useCallback(() => {
    const url = selectedAccessId
      ? `${OnboardingUrls[RouteNames.Ticketing]}?accessid=${selectedAccessId}`
      : OnboardingUrls[RouteNames.Ticketing];
    history.push(url);
  }, [history, selectedAccessId]);

  const handleEditEmail = useCallback(() => {
    setEditValue(notificationEmail ?? "");
    setValidationError(undefined);
    setIsEditing(true);
  }, [notificationEmail]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setValidationError(undefined);
  }, []);

  const handleSaveEmail = useCallback(() => {
    const trimmed = editValue.trim();
    if (!trimmed || !emailRegex.test(trimmed)) {
      setValidationError(strings.ticketInvalidEmailMsg);
      return;
    }
    setValidationError(undefined);
    dispatch(ticketingActions.updateNotificationEmail.start(trimmed));
    setIsEditing(false);
  }, [editValue, dispatch, strings.ticketInvalidEmailMsg]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSaveEmail();
      } else if (e.key === "Escape") {
        handleCancelEdit();
      }
    },
    [handleSaveEmail, handleCancelEdit]
  );

  const handleOptOutToggle = useCallback(() => {
    dispatch(ticketingActions.updateNotificationOptOut.start(!optOut));
  }, [dispatch, optOut]);

  if (!isSignedIn || !isVerifyChecked) {
    return (
      <>
        <div className={styles.outerContainer}>
          <div className={styles.spinnerContainer}>
            <LoadingSpinner />
          </div>
        </div>
      </>
    );
  }

  if (!isVerified) {
    return (
      <AccessDeniedPage
        footNote={strings.ticketAccessDeniedMsg}
        pageType={PageType.IdXboxApplication}
      />
    );
  }

  return (
    <>
      <div className={styles.outerContainer}>
        <div className={styles.innerContainer}>
          <button className={styles.backLink} onClick={handleBack}>
            {strings.ticketSettingsBackLabel}
          </button>
          <h1 className={styles.pageTitle}>{strings.ticketSettingsPageTitle}</h1>
          <div className={styles.container}>
            {/* Email Preferences Section */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>{strings.ticketSettingsEmailPreferencesTitle}</h2>
              <div className={styles.emailRow}>
                <span className={styles.emailLabel}>{strings.ticketSettingsEmailLabel}</span>
                {isEditing ? (
                  <>
                    <input
                      type="email"
                      className={styles.emailInput}
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      aria-label={strings.ticketNotificationEmailAriaLabel}
                    />
                    <button
                      className={styles.actionButton}
                      onClick={handleSaveEmail}
                      disabled={isSaving}
                    >
                      {isSaving ? strings.ticketSavingLabel : strings.ticketSaveLabel}
                    </button>
                    <button
                      className={styles.actionButton}
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                    >
                      {strings.ticketCancelLabel}
                    </button>
                  </>
                ) : (
                  <>
                    <span className={styles.emailValue}>{notificationEmail ?? "—"}</span>
                    {isEffectivelyVerified ? (
                      <span className={styles.verifiedBadge}>{strings.ticketVerifiedBadge}</span>
                    ) : (
                      <span className={styles.unverifiedBadge}>
                        {strings.ticketNotVerifiedBadge}
                      </span>
                    )}
                    <button className={styles.actionButton} onClick={handleEditEmail}>
                      {strings.ticketChangeLabel}
                    </button>
                  </>
                )}
              </div>
              {(validationError || serverError) && (
                <div className={styles.errorMessage}>{validationError ?? serverError}</div>
              )}
              {!isEffectivelyVerified && !isEditing && (
                <div className={styles.verificationNotice}>
                  {strings.ticketSettingsVerificationNotice}
                </div>
              )}
              <div className={styles.optOutRow}>
                <input
                  type="checkbox"
                  id="optOutCheckbox"
                  className={styles.optOutCheckbox}
                  checked={optOut}
                  onChange={handleOptOutToggle}
                  disabled={isSaving}
                />
                <label htmlFor="optOutCheckbox" className={styles.optOutLabel}>
                  {strings.ticketSettingsOptOutLabel}
                </label>
              </div>
            </div>

            {/* Teams Management Section */}
            {(isAdmin || isManager) && <TeamsAdminSection />}
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketingSettingsPage;
